import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BuildingComplexPlus } from 'lucide-react';
import RowIconAction, { iconActionClassName } from 'src/app/admin/components/row-icon-action/RowIconAction';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { AdminViews, GNPViews } from 'src/routes/routesConfig';
import LockReasonIcon, { LockReason } from 'src/shared/components/lock-reason-icon/LockReasonIcon';
import Loader from 'src/shared/components/loader/Loader';
import Alert from 'src/shared/components/ui/alert';
import useCanAdministerOrganisations from 'src/app/admin/hooks/useCanAdministerOrganisations';
import {
    useGetProductLicensingQuery,
    useClearProductVisibilityMutation,
    useSetProductVisibilityMutation,
} from 'src/app/admin/views/risk-portfolio/state/api/organisationEngineSlice';
import {
    PRODUCT_HAS_UPSELL_PAGE,
    PRODUCT_RELEASE,
    PRODUCT_TURNED_ON_PER_PROJECT,
    ProductKey,
    ProductVisibility,
    ReleasePolicy,
} from 'src/shared/types/engine/products';
import { PRODUCT_LISTINGS, ProductListing } from './productCatalogue';
import { DefaultStateIcon } from './ProductSettingIcons';
import TurnedOnPerProjectCard from './TurnedOnPerProjectCard';
import VisibilityToggle, { Choice } from './VisibilityToggle';

type OrganisationLicencesProps = {
    customerId: string;
};

/** A listing plus where it sits, so the list can render flat and still read as a tree. */
type ProductRow = { listing: ProductListing; parentId: string | null };

/**
 * Depth-first, parents before their members.
 *
 * Rendered flat rather than nested so every row is a sibling in one list and
 * the columns line up down the whole table; the indent and `data-parent-product`
 * carry the hierarchy instead.
 */
const toRows = (listings: ProductListing[], parentId: string | null = null): ProductRow[] =>
    listings.flatMap(listing => [{ listing, parentId }, ...toRows(listing.children ?? [], listing.id)]);

/** Where the thing blocking this row is actually done. */
type RowDestination = { label: string; to: string; icon: React.ReactNode };

/**
 * A locked row is a dead end without this: it says what is wrong but not where
 * to fix it. An unmanaged product points at the GNP admin page that does manage
 * it. An organisation that is not onboarded has one callout above the list
 * instead, because onboarding is for the organisation, not for each product.
 */
const destinationFor = ({ listing, customerId }: { listing: ProductListing; customerId: string }): RowDestination | null => {
    if (!listing.licensable) {
        return {
            label: 'Manage in GNP',
            to: navRoutesConfig[GNPViews.ORG_MANAGEMENT]({ customerId }),
            icon: <ArrowUpRight size={16} className='shrink-0' />,
        };
    } else {
        return null;
    }
};

const whyLocked = ({
    listing,
    canManage,
    isOnboarded,
}: {
    listing: ProductListing;
    canManage: boolean;
    isOnboarded: boolean;
}): LockReason | null => {
    if (!listing.licensable) {
        return { message: 'This product is not managed here yet, use Project Definition instead', tone: 'info' };
    }
    if (!isOnboarded) {
        return {
            // A warning rather than information: this one is a gap someone has to close.
            message: 'This organisation is not yet onboarded into this product, so there is nothing to release.',
            tone: 'warning',
        };
    }
    if (!canManage) {
        return { message: 'You do not have permission to change this.', tone: 'info' };
    }
    return null;
};

/** What the customer sees for each setting, and for none. */
const describeSetting = (setting: ProductVisibility | undefined, releasePolicy: ReleasePolicy): string => {
    switch (setting) {
        case ProductVisibility.VISIBLE:
            return 'Results visible';
        case ProductVisibility.HIDDEN:
            return 'Hidden from customer';
        default:
            return releasePolicy === ReleasePolicy.AUTOMATIC ? 'Released automatically' : 'Upsell page shown';
    }
};

/**
 * Every product an organisation could have, and the switches for the ones that
 * can be released from here.
 *
 * The whole catalogue is listed rather than only the switchable part, so the
 * list answers "what does this customer have?" instead of "what have we built
 * a switch for?". A product we cannot yet manage is shown locked, with its
 * reason on the icon beside it.
 *
 * A setting releases a product to the customer or hides it from them, and a
 * product with no setting is pitched to them. Internal staff see an onboarded
 * organisation's results whatever is decided, so these controls govern what
 * the *customer* sees, not what we can.
 */
const OrganisationLicences = ({ customerId }: OrganisationLicencesProps) => {
    const { canManage } = useCanAdministerOrganisations();
    const { data: productLicensing, isLoading } = useGetProductLicensingQuery(customerId, { skip: !customerId });
    const [setVisibility] = useSetProductVisibilityMutation();
    const [clearVisibility] = useClearProductVisibilityMutation();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [pendingProduct, setPendingProduct] = useState<ProductKey | null>(null);

    // The engine answers `null` for an organisation it does not know.
    const isOnboarded = Boolean(productLicensing);

    const change = async (product: ProductKey, label: string, request: () => Promise<unknown>) => {
        setErrorMessage(null);
        setPendingProduct(product);
        try {
            await request();
        } catch {
            // The toggle reads from the server's answer, so it has not moved,
            // but silence would read as success, so say what happened.
            setErrorMessage(`${label} could not be updated. Please try again.`);
        } finally {
            setPendingProduct(null);
        }
    };

    const choose = (product: ProductKey, label: string, choice: Choice) =>
        change(product, label, () =>
            choice
                ? setVisibility({ bcId: customerId, product, visibility: choice }).unwrap()
                : clearVisibility({ bcId: customerId, product }).unwrap()
        );

    if (isLoading) {
        return (
            <div className='flex justify-center py-6'>
                <Loader />
            </div>
        );
    }

    return (
        <div className='flex flex-col gap-4' data-testid='organisation-licences'>
            {!isOnboarded && (
                <Alert severity='warning'>
                    <span className='flex flex-wrap items-center gap-x-3 gap-y-1' data-testid='organisation-not-onboarded'>
                        This organisation is not onboarded yet, so its products cannot be released.
                        {/* Its own icon, not the generic arrow: onboarding adds the organisation
                            to the products, where the other links merely go somewhere else. */}
                        <Link
                            to={navRoutesConfig[AdminViews.RISK_PORTFOLIO]({ customerId })}
                            className='inline-flex items-center gap-1.5 font-medium underline'
                        >
                            <BuildingComplexPlus size={16} className='shrink-0' aria-hidden />
                            Onboard organisation
                        </Link>
                    </span>
                </Alert>
            )}
            <div className='flex flex-col divide-y divide-grey-20'>
                {toRows(PRODUCT_LISTINGS).map(({ listing, parentId }) => {
                    const setting = listing.licensable ? productLicensing?.[listing.key] : undefined;
                    const isTurnedOnPerProject = listing.licensable && PRODUCT_TURNED_ON_PER_PROJECT[listing.key];
                    const lockReason = whyLocked({ listing, canManage, isOnboarded });
                    // A family is acted on as a whole: repeating the same link on each
                    // of its members would be three ways to reach one page.
                    const destination = parentId ? null : destinationFor({ listing, customerId });
                    const lockReasonForRow = parentId ? null : lockReason;

                    return (
                        <div
                            key={listing.id}
                            className={`flex items-center justify-between gap-4 py-3${parentId ? ' pl-6' : ''}`}
                            data-testid={`organisation-product-row-${listing.id}`}
                            data-parent-product={parentId ?? undefined}
                        >
                            <div
                                className={`flex min-w-0 flex-col gap-0.5${listing.licensable ? '' : ' text-muted-foreground'}`}
                                data-testid='product-details'
                            >
                                {/* The icons sit inside the name, so they follow its last word
                                    when a long name wraps, rather than centring beside both lines. */}
                                <span className='text-sm font-medium' data-testid='product-label'>
                                    {listing.label}
                                    {(lockReasonForRow || isTurnedOnPerProject) && (
                                        <span className='ml-1.5 inline-flex items-center gap-1.5 align-[-2px]'>
                                            {lockReasonForRow && (
                                                <LockReasonIcon reason={lockReasonForRow} testId={`product-lock-${listing.id}`} />
                                            )}
                                            {isTurnedOnPerProject && (
                                                <TurnedOnPerProjectCard
                                                    label={listing.label}
                                                    customerId={customerId}
                                                    isSetToHidden={setting === ProductVisibility.HIDDEN}
                                                />
                                            )}
                                        </span>
                                    )}
                                </span>
                                {/* Only a product that can be released has a release state to report. */}
                                {listing.licensable && (
                                    <span className='flex min-w-0 items-center gap-1.5'>
                                        <span className='truncate text-sm text-grey-70' data-testid='product-state'>
                                            {describeSetting(setting, PRODUCT_RELEASE[listing.key])}
                                        </span>
                                        {setting === undefined && (
                                            <DefaultStateIcon
                                                releasePolicy={PRODUCT_RELEASE[listing.key]}
                                                hasUpsellPage={PRODUCT_HAS_UPSELL_PAGE[listing.key]}
                                                isTurnedOnPerProject={PRODUCT_TURNED_ON_PER_PROJECT[listing.key]}
                                            />
                                        )}
                                    </span>
                                )}
                            </div>
                            <div className='flex items-center gap-3 shrink-0'>
                                {destination && (
                                    <RowIconAction label={destination.label}>
                                        <Link to={destination.to} aria-label={destination.label} className={iconActionClassName}>
                                            {destination.icon}
                                        </Link>
                                    </RowIconAction>
                                )}
                                {/* No control where there is nothing to decide: a product this
                                    screen does not manage would only ever render a dead control,
                                    which reads as "off" rather than "not ours to set". */}
                                {listing.licensable && (
                                    <VisibilityToggle
                                        label={listing.label}
                                        releasePolicy={PRODUCT_RELEASE[listing.key]}
                                        hasUpsellPage={PRODUCT_HAS_UPSELL_PAGE[listing.key]}
                                        isTurnedOnPerProject={PRODUCT_TURNED_ON_PER_PROJECT[listing.key]}
                                        setting={setting}
                                        disabled={Boolean(lockReason) || pendingProduct === listing.key}
                                        onChoose={choice => choose(listing.key, listing.label, choice)}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {errorMessage && (
                <p role='alert' className='text-sm text-red-100'>
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default OrganisationLicences;
